import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useSSE } from '../hooks/useSSE';
import { AuthContext } from '../context/AuthContext';
import { Icon } from '../design-system/icons.jsx';
import { fmtDate } from '../utils/format';

export default function NotificationBell({ buttonClassName = 'sh-tb-ctl sh-tb-iconbtn' }) {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  useSSE(
    '/api/notifications/stream',
    (data) => {
      if (data.type === 'scan_complete') {
        fetchNotifications();
      }
    },
    { isTerminal: () => false },
  );

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await api.get('/api/notifications');
      setNotifications(data.notifications);
      setLoadError(false);
    } catch (err) {
      console.warn('NotificationBell: failed to load notifications', err);
      setLoadError(true);
    }
  };

  const markRead = async (id) => {
    const snapshot = notifications;
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    try {
      await api.post(`/api/notifications/${id}/read`);
    } catch (err) {
      console.warn('NotificationBell: failed to mark notification read', err);
      setNotifications(snapshot);
    }
  };

  const markAllRead = async () => {
    const snapshot = notifications;
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    try {
      await api.post('/api/notifications/read-all');
    } catch (err) {
      console.warn('NotificationBell: failed to mark all read', err);
      setNotifications(snapshot);
    }
  };

  const unread = notifications.filter(n => !n.is_read).length;

  return (
    <div ref={ref} style={styles.wrap}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('notifications.title')}
        title={t('notifications.title')}
        className={buttonClassName}
        style={styles.bell}
      >
        <Icon name="alert" size={17} />
        {unread > 0 && <span style={styles.badge}>{unread}</span>}
      </button>

      {open && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span>{t('notifications.title')}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {unread > 0 && <span style={styles.unreadCount}>{t('notifications.unread', { count: unread })}</span>}
              {notifications.length > 0 && (
                <button
                  onClick={markAllRead}
                  disabled={unread === 0}
                  style={{ ...styles.markAllBtn, opacity: unread === 0 ? 0.35 : 1, cursor: unread === 0 ? 'default' : 'pointer' }}
                >
                  {t('notifications.markAllRead')}
                </button>
              )}
            </div>
          </div>
          <div style={styles.list}>
            {loadError && notifications.length === 0 ? (
              <div style={styles.errorRow}>{t('notifications.loadError')}</div>
            ) : notifications.length === 0 ? (
              <div style={styles.empty}>{t('notifications.empty')}</div>
            ) : (
              notifications.slice(0, 15).map(n => (
                <div
                  key={n.id}
                  onClick={() => !n.is_read && markRead(n.id)}
                  style={{ ...styles.item, ...(n.is_read ? {} : styles.itemUnread) }}
                >
                  {!n.is_read && <div style={styles.dot} />}
                  <div style={styles.itemContent}>
                    <div style={styles.itemMsg}>{n.message}</div>
                    <div style={styles.itemTime}>{fmtDate(n.created_at)}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: { position: 'relative' },
  bell: {
    position: 'relative',
  },
  badge: {
    position: 'absolute',
    top: -4,
    right: -4,
    background: 'var(--accent)',
    color: '#fff',
    fontSize: 9,
    fontWeight: 700,
    borderRadius: 99,
    minWidth: 14,
    height: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 3px',
  },
  panel: {
    position: 'absolute',
    right: 0,
    top: 'calc(100% + 6px)',
    width: 320,
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)',
    zIndex: 'var(--z-dropdown)',
    overflow: 'hidden',
  },
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-subtle)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  unreadCount: { fontSize: 11, color: 'var(--accent)', fontWeight: 500 },
  list: { maxHeight: 360, overflowY: 'auto' },
  empty: { padding: '24px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 },
  errorRow: { padding: '24px 16px', textAlign: 'center', color: 'var(--danger)', fontSize: 13 },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-subtle)',
    cursor: 'pointer',
    transition: 'background 0.1s',
  },
  itemUnread: { background: 'var(--accent-dim)' },
  dot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: 'var(--accent)',
    marginTop: 5,
    flexShrink: 0,
  },
  itemContent: { flex: 1 },
  itemMsg: { fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.4 },
  itemTime: { fontSize: 11, color: 'var(--text-muted)', marginTop: 3 },
  markAllBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--accent)',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '2px 0',
    fontFamily: 'inherit',
  },
};
